import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { UsersService } from '@/modules/users/services/users/users.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-foto-perfil',
    templateUrl: './foto-perfil.component.html',
    styleUrls: ['./foto-perfil.component.css'],
    standalone: true,
    imports: [CommonModule, ButtonModule, ProgressSpinnerModule, ToastModule, TooltipModule],
    providers: [MessageService],
})
export class FotoPerfilComponent implements OnDestroy {
    @Input({ required: true }) userId!: number;
    @Input() photoUrl: string | null = null;
    @Input() nombre: string = '';
    @Input() size: 'sm' | 'md' | 'lg' = 'md';
    @Input() editable: boolean = true;

    @Output() photoUpdated = new EventEmitter<string>();
    @Output() uploadStarted = new EventEmitter<void>();

    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

    previewUrl: string | null = null;
    isUploading = false;

    private usersService = inject(UsersService);
    private messageService = inject(MessageService);

    get displayUrl(): string | null {
        return this.previewUrl ?? this.photoUrl ?? null;
    }

    get initials(): string {
        return this.nombre ? this.nombre.charAt(0).toUpperCase() : '?';
    }

    triggerFileInput(): void {
        if (!this.editable) return;
        this.fileInputRef.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) return;

        // Reset input so the same file can be selected again if needed
        input.value = '';

        if (!file.type.startsWith('image/')) {
            this.messageService.add({
                severity: 'error',
                summary: 'Archivo inválido',
                detail: 'Solo se permiten archivos de imagen.',
                life: 4000,
            });
            return;
        }

        const maxSizeBytes = 2 * 1024 * 1024; // 2 MB
        if (file.size > maxSizeBytes) {
            this.messageService.add({
                severity: 'error',
                summary: 'Archivo muy grande',
                detail: 'La imagen no puede superar los 2 MB.',
                life: 4000,
            });
            return;
        }

        const blobUrl = URL.createObjectURL(file);
        this.previewUrl = blobUrl;
        this.isUploading = true;
        this.uploadStarted.emit();

        this.usersService.uploadProfilePhoto(this.userId, file).subscribe({
            next: (response: any) => {
                let newUrl: string = response?.data?.foto_perfil_url ?? '';
                if (newUrl) {
                    const backendOrigin = new URL(environment.apiBack).origin;
                    newUrl = newUrl.replace(/^https?:\/\/[^/]+/, backendOrigin);
                }
                this.photoUpdated.emit(newUrl);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Foto actualizada',
                    detail: 'La foto de perfil se guardó correctamente.',
                    life: 3000,
                });
            },
            error: () => {
                // Revoke the optimistic preview and fall back
                this.revokePreview();
                this.previewUrl = null;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error al subir',
                    detail: 'No se pudo guardar la foto. Intenta de nuevo.',
                    life: 5000,
                });
            },
            complete: () => {
                this.isUploading = false;
            },
        });
    }

    ngOnDestroy(): void {
        this.revokePreview();
    }

    private revokePreview(): void {
        if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
        }
    }
}
